import { ticketsApi } from "#/api/ticketsApi";
import type { SuggestedItem } from "#/types/tickets";
import { sanitizeItemStatusLabel } from "#/utils/StringUtils";
import { Badge, Checkbox, Loader, Text } from "@mantine/core";
import { IconExternalLink } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

export interface Props {
  /** Category the ticket is being raised for. Null hides the block. */
  productId: number | null;
  /** Status the ticket will move the items to. Empty hides the block. */
  status: string;
  /** Ids of the suggested items currently ticked. */
  selectedIds: number[];
  onSelectionChange: (ids: number[]) => void;
  /** Reports the fetched suggestions so the parent can label them if needed. */
  onSuggestionsChange?: (items: SuggestedItem[]) => void;
}

/**
 * Shows the items the system recommends prioritising for a category ticket —
 * the longest-dwelling items still sitting behind the chosen status. They are
 * ticked by default (that is the point of the suggestion); the user can untick
 * any of them, or all of them, before sending the ticket.
 */
const SuggestedItemsPicker = ({
  productId,
  status,
  selectedIds,
  onSelectionChange,
  onSuggestionsChange,
}: Props) => {
  const { t } = useTranslation();
  const enabled = Boolean(productId) && Boolean(status);

  const { data: suggestions, isFetching } = useQuery({
    queryKey: ["ticket-suggested-items", productId, status],
    queryFn: async () => {
      const response = await ticketsApi.getSuggestedItems(productId!, status);
      return response.data;
    },
    enabled,
    staleTime: 1000 * 60,
    retry: false,
  });

  // Pre-tick every suggestion whenever a new set arrives (category or status
  // changed). Unticking does not change `suggestions`, so it is not undone.
  useEffect(() => {
    if (!enabled) {
      onSelectionChange([]);
      onSuggestionsChange?.([]);
      return;
    }
    if (!suggestions) return;
    onSelectionChange(suggestions.map((item) => item.id));
    onSuggestionsChange?.(suggestions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestions, enabled]);

  if (!enabled) return null;

  return (
    <div className="w-full flex flex-col gap-2 rounded-md border border-gray-200 bg-gray-50 p-3">
      <div className="flex items-center gap-2">
        <Text size="sm" fw={600}>
          {t("tickets.suggestions.title")}
        </Text>
        {isFetching && <Loader size={14} />}
      </div>

      <Text size="xs" c="dimmed">
        {t("tickets.suggestions.description")}
      </Text>

      {!isFetching && (!suggestions || suggestions.length === 0) ? (
        <Text size="xs" c="dimmed" fs="italic">
          {t("tickets.suggestions.empty")}
        </Text>
      ) : (
        <div className="flex flex-col gap-2">
          {suggestions?.map((item) => (
            <div
              key={item.id}
              className="flex items-center justify-between gap-2 rounded border border-gray-200 bg-white px-2 py-1.5"
            >
              <Checkbox
                color="black"
                checked={selectedIds.includes(item.id)}
                onChange={(event) =>
                  onSelectionChange(
                    event.currentTarget.checked
                      ? [...selectedIds, item.id]
                      : selectedIds.filter((id) => id !== item.id)
                  )
                }
                label={
                  <a
                    href={`/inventory/items/${item.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-semibold text-black hover:underline"
                    onClick={(event) => event.stopPropagation()}
                  >
                    {t("tickets.card.itemNumber", { id: item.id })}
                    <IconExternalLink size={12} />
                  </a>
                }
              />

              <div className="flex items-center gap-2">
                <Badge size="xs" variant="light" color="gray">
                  {item.status ? sanitizeItemStatusLabel(item.status) : "--"}
                </Badge>
                <Text size="xs" c="dimmed">
                  {t("tickets.suggestions.idleDays", { count: item.idle_days })}
                </Text>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SuggestedItemsPicker;
